import React, { useState, useMemo, useCallback } from 'react';
import './MobileGravityInfo.css';

const MobileGravityInfo = ({
  network,
  numConnections
}) => {
  const [sortField, setSortField] = useState('force');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedNode, setSelectedNode] = useState(null);

  // Handle column header click for sorting
  const handleHeaderClick = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'symbol' ? 'asc' : 'desc');
    }
  }, [sortField, sortDirection]);

  // Filter out central symbol and sort data
  const sortedData = useMemo(() => {
    if (!network?.node_table_data || network.node_table_data.length === 0) {
      return [];
    }

    // Filter out central symbol - backend should handle this but double-check
    const filteredData = network.node_table_data.filter(node => 
      !node.is_central_node && node.symbol !== network.central_symbol
    );
    
    return [...filteredData].sort((a, b) => {
      let valueA = a[sortField];
      let valueB = b[sortField];
      
      // Handle numeric vs string sorting
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        // For force_ranking, lower is better
        if (sortField === 'force_ranking') {
          return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
        }
        return sortDirection === 'desc' ? valueB - valueA : valueA - valueB;
      } else {
        // String comparison
        const strA = String(valueA || '');
        const strB = String(valueB || '');
        return sortDirection === 'asc' 
          ? strA.localeCompare(strB) 
          : strB.localeCompare(strA);
      }
    });
  }, [network?.node_table_data, network?.central_symbol, sortField, sortDirection]);

  // Handle row click
  const handleRowClick = useCallback((node) => {
    setSelectedNode(node);
  }, []);

  // Render sort indicator
  const renderSortIndicator = useCallback((field) => {
    if (sortField !== field) return null;
    return <span className="mobile-sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  }, [sortField, sortDirection]);

  if (!network) {
    return null;
  }

  return (
    <div className="mobile-info-container">
      <div className="mobile-info-content">

        {/* Node Rankings Table */}
        <div className="mobile-rankings-section">
          <h3>Node Rankings</h3>

          {sortedData.length > 0 ? (
            <div className="mobile-table-container">
              <table className="mobile-data-table">
                <thead>
                  <tr>
                    <th 
                      onClick={() => handleHeaderClick('symbol')}
                      className="mobile-th clickable"
                    >
                      Symbol {renderSortIndicator('symbol')}
                    </th>
                    <th 
                      onClick={() => handleHeaderClick('force')}
                      className="mobile-th clickable center"
                    >
                      Force {renderSortIndicator('force')}
                    </th>
                    <th 
                      onClick={() => handleHeaderClick('force_ranking')}
                      className="mobile-th clickable center"
                    >
                      Rank {renderSortIndicator('force_ranking')}
                    </th>
                    <th 
                      onClick={() => handleHeaderClick('distance')}
                      className="mobile-th clickable center"
                    >
                      Distance {renderSortIndicator('distance')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((node, index) => (
                    <tr 
                      key={node.symbol || index} 
                      className={`mobile-tr ${selectedNode?.symbol === node.symbol ? 'selected' : ''}`}
                      onClick={() => handleRowClick(node)}
                    >
                      <td className="mobile-td mobile-symbol-cell">
                        {node.symbol}
                      </td>
                      <td className="mobile-td mobile-force-cell center">
                        {node.force || 'N/A'}
                      </td>
                      <td className="mobile-td mobile-rank-cell center">
                        {node.force_ranking || 'N/A'}
                      </td>
                      <td className="mobile-td mobile-distance-cell center">
                        {node.distance || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mobile-no-data">
              <p>No ranking data available</p>
            </div>
          )}
        </div>

        {/* Node Details - Shows when a symbol is clicked */}
        <div className="mobile-node-details-section">
          <h3>Node Details</h3>
          {selectedNode ? (
            <div className="mobile-node-details">
              <div className="mobile-detail-item">
                <span className="mobile-detail-label">Symbol:</span>
                <span className="mobile-detail-value">{selectedNode.symbol}</span>
              </div>
              <div className="mobile-detail-item">
                <span className="mobile-detail-label">Name:</span>
                <span className="mobile-detail-value">{selectedNode.name || 'N/A'}</span>
              </div>
              <div className="mobile-detail-item">
                <span className="mobile-detail-label">Exchange:</span>
                <span className="mobile-detail-value">{selectedNode.exchange || 'N/A'}</span>
              </div>
              <div className="mobile-detail-item">
                <span className="mobile-detail-label">Type:</span>
                <span className="mobile-detail-value">{selectedNode.security_type || 'N/A'}</span>
              </div>
              {selectedNode.sector && (
                <div className="mobile-detail-item">
                  <span className="mobile-detail-label">Sector:</span>
                  <span className="mobile-detail-value">{selectedNode.sector}</span>
                </div>
              )}
              {selectedNode.industry && (
                <div className="mobile-detail-item">
                  <span className="mobile-detail-label">Industry:</span>
                  <span className="mobile-detail-value">{selectedNode.industry}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="mobile-node-details-placeholder">
              <p>Tap a symbol above to view details</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default MobileGravityInfo;